import React, { useState, useEffect } from 'react';
import { organizationAPI } from '../../services/api';
import Pagination from '../Pagination';
import './OrganizationSelectorDialog.css';

interface Organization {
  _id: string;
  name: string;
}

interface OrganizationSelectorDialogProps {
  show: boolean;
  selectedOrganizationId?: string;
  onSelect: (organization: Organization) => void;
  onClose: () => void;
}

function OrganizationSelectorDialog({
  show,
  selectedOrganizationId,
  onSelect,
  onClose
}: OrganizationSelectorDialogProps) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<'name' | 'created_at'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const response = await organizationAPI.list(sortBy, sortOrder);
      setOrganizations(response.data);
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      loadOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, sortBy, sortOrder]);

  // Filtrar e paginar organizações
  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchName.toLowerCase()) ||
    org._id.toLowerCase().includes(searchName.toLowerCase())
  );
  
  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrganizations = filteredOrganizations.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchName]);

  const handleSelect = (org: Organization) => {
    onSelect(org);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content org-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>🏢 Selecionar Organização</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Barra de busca e ordenação */}
          <div className="search-bar">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="🔍 Buscar por nome ou ID..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="search-input"
              />
              {searchName && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchName('')}
                  title="Limpar busca"
                >
                  ✕
                </button>
              )}
            </div>
            
            <select 
              className="sort-select"
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-') as ['name' | 'created_at', 'asc' | 'desc'];
                setSortBy(field);
                setSortOrder(order);
              }}
            >
              <option value="name-asc">Nome (A-Z)</option>
              <option value="name-desc">Nome (Z-A)</option>
              <option value="created_at-desc">Mais recentes</option>
              <option value="created_at-asc">Mais antigas</option>
            </select>
            
            <span className="search-count">
              {filteredOrganizations.length} organizaç{filteredOrganizations.length !== 1 ? 'ões' : 'ão'}
            </span>
          </div>

          {/* Tabela de organizações */}
          {loading ? (
            <p>Carregando...</p>
          ) : (
            <>
              <div className="table-container">
                <table className="organizations-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrganizations.length > 0 ? (
                      paginatedOrganizations.map((org) => (
                        <tr 
                          key={org._id}
                          className={selectedOrganizationId === org._id ? 'selected' : ''}
                        >
                          <td>
                            <span className="name-icon">🏢</span>
                            {org.name}
                            {selectedOrganizationId === org._id && (
                              <span className="current-badge">Atual</span>
                            )}
                            <br />
                            <span className="org-id-label">{org._id}</span>
                          </td>
                          <td className="actions-cell">
                            <button 
                              className="btn-action btn-select-icon"
                              onClick={() => handleSelect(org)}
                              title={selectedOrganizationId === org._id ? 'Organização atual' : 'Selecionar esta organização'}
                              disabled={selectedOrganizationId === org._id}
                            >
                              {selectedOrganizationId === org._id ? '✓' : '➜'}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} className="no-results">
                          Nenhuma organização encontrada{searchName && ` com o nome "${searchName}"`}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrganizationSelectorDialog;
